import {
	Component,
	ElementRef,
	EventEmitter,
	forwardRef,
	Input,
	Output,
	SimpleChange,
	ViewChild,
	ChangeDetectionStrategy,
} from "@angular/core"
import { ActivatedRoute } from "@angular/router"
import { Modalable } from "@app/Modalable"
import { COMMON_IMPORTS } from "../common-imports"
import {
	HomePersonColumns,
	patientColors,
	ReportItemType,
	yes_no_opts,
} from "@app/definitions/types"
import {
	GenTable2Component,
	GenTableComponent,
} from "@app/gen-table/gen-table.component"
import { LangValPipe } from "@app/utils/lang-val.pipe"
import { MobileTabsComponent } from "@app/utils/mobile-tabs/mobile-tabs.component"
import { BannerComponent } from "@app/utils/banner/banner.component"
import { GenButtonGroupComponent } from "@app/gen-inputs/gen-button-group/gen-button-group.component"
import {
	UpdateContactComponent,
	UpdatePayorComponent,
} from "@app/person-update-commons/person-update-commons.component"
import { ModalWrapComponent } from "@app/modal-wrap/modal-wrap.component"
import { ReportsComponent } from "@app/reports/reports.component"
import { AddInvoiceComponent } from "@app/add-invoice/add-invoice.component"
import { UploadDocumentComponent } from "@app/upload-document/upload-document.component"
import { GenTableDisplayComponent } from "@app/gen-table-display/gen-table-display.component"
import { DateFormatPipe } from "@app/utils/date-format.pipe"
import { FinNumberPipe } from "@app/utils/finNumber.pipe"
import { SlicePipe } from "@angular/common"
import { AddDocumentComponent } from "@app/add-document/add-document.component"

const baseSideItems: any[] = [
	//the items on the side column (icon comes from config, should flag (iconIsImg) if it's img. instead of icon. )
	{
		name: "contactway",
		title: "contactway",
		icon: "contactway",
		iconIsImg: true,
	},
	// { name: 'reminder', title: 'reminders', icon: 'reminder', iconIsImg: true },
	{ name: "invoice", title: "financial", icon: "money", iconIsImg: true },
]
const PayorSiteItems: any[] = [
	...baseSideItems,
	{
		name: "document",
		title: "documents",
		icon: "document",
		iconIsImg: true,
	},
]

@Component({
	selector: "app-person-side-item",
	imports: [COMMON_IMPORTS, LangValPipe],
	changeDetection: ChangeDetectionStrategy.Eager,
	template: `
		<div
			class="person-side-item"
			[style.box-shadow]="color + ' 0 1px 0 0'"
			[class.active]="curCenter == sideItem.name"
		>
			<!-- [style.background-color]="color" -->
			<div [titleLang]="sideItem.title">
				<app-icon
					[icon]="sideItem.icon"
					[isImg]="sideItem.iconIsImg"
					[titleLang]="sideItem.title"
				/>
				<div>{{ sideItem.title | langVal }}</div>
			</div>
			<div>
				<ng-content></ng-content>
			</div>
		</div>
	`,
})
export class PersonSideItemComponent {
	@Input() color: string
	@Input() sideItem: any
	@Input() curCenter: string
}

const IMPORTS = [
	COMMON_IMPORTS,
	MobileTabsComponent,
	LangValPipe,
	BannerComponent,
	forwardRef(() => GenTableComponent),
	forwardRef(() => GenTable2Component),
	GenButtonGroupComponent,
	UpdateContactComponent,
	UpdatePayorComponent,
	ModalWrapComponent,
	ReportsComponent,
	AddInvoiceComponent,
	UploadDocumentComponent,
	forwardRef(() => GenTableDisplayComponent),
	PersonSideItemComponent,
	DateFormatPipe,
	FinNumberPipe,
	SlicePipe,
	forwardRef(() => AddDocumentComponent),
]

@Component({
	selector: "app-person-common",
	imports: [IMPORTS],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: "./person-common.component.html",
})
export class PersonCommonComponent extends Modalable {
	modalProps: any = null

	personType: ReportItemType = "" //used to generalize data
	@Input() id: number = 0
	@Input() showColumn: HomePersonColumns = "all" //which column is currently shows (relevant for mobile display)
	@Output() updateShowColumn = new EventEmitter() //inform parent of a change in display column
	curCenter: string = "" //current center content area name
	mobileChangeSub: any = null //listens to changes in isMobile from the eventsService
	@ViewChild("center") center: ElementRef //the center container (used for scrolling)
	@ViewChild("sideItemsDiv") sideItemsDiv: ElementRef
	@ViewChild("finresourceTable") finresourceTable: GenTableComponent
	@ViewChild("documentsTable") documentsTable: GenTableComponent

	@Input() contact: any = null //the object that holds the field values (and is saved)
	@Input() payor: any = null //the object that holds the field values (and is saved)
	@Input() patient: any = null //the object that holds the field values (and is saved)
	is_finresource_connected_only: yes_no_opts = "no"

	sideItems: any[] = baseSideItems
	color: string = ""

	finresouecrActionSub: any = null
	payorAddListener: any = null
	personReloadListener: any = null

	preserveCenter: boolean = false

	getPersonSub: any
	onIdChangeLoad(res: any) {
		this[this.personType] = res //load it to the local variable (ex: this.contact)
		this.apiService.sendApiGetPerms(this.load.bind(this))
	}

	async onIdChange() {
		let funcName: string = "get_patient_data" //name of the api function to use
		switch (this.personType) {
			case "contact":
				funcName = "get_contact_data"
				break
			case "payor":
				funcName = "get_payor_data"
				break
		}
		this.getPersonSub?.unsubscribe()
		this.getPersonSub = this.apiService.postSubscribe(
			funcName,
			{ id: +this.id },
			this.onIdChangeLoad.bind(this)
		)

		// const res: any = await this.apiService.post(funcName, { id })

		// this.load();	//proceed to load
	}
	isMobileHandle() {
		//sets default for mobile view and subscribed to changes in isMobile
		if (this.eventsService.isMobile) {
			//if mobile - default to column 2 (center)

			this.showColumn = "center"
		}
		this.mobileChangeSub = this.eventsService.isMobileSubj.subscribe(() => {
			//subscribe to changes in isMobile
			this.showColumn = this.eventsService.isMobile ? "center" : "all" //default is 2 (center)
		})
	}
	ngOnDestroy() {
		//make sure to unsubscribe from the isMobile changes
		this.unsub()
	}
	unsub() {
		this.mobileChangeSub?.unsubscribe()
		this.finresouecrActionSub?.unsubscribe()
		this.payorAddListener?.unsubscribe()
		this.personReloadListener?.unsubscribe()
	}

	ngOnInit() {
		this.isMobileHandle() //PersonCommons method to handle changes in isMobile
		this.load()
	}

	load() {
		this.unsub()
		//process data after load
		if (!this.preserveCenter) {
			this.curCenter = "" //reset current center content area
		} else {
			this.preserveCenter = false
		}
		if (!this[this.personType]) {
			return
		} //nothing in the person object - exit
		this.is_finresource_connected_only = "no"

		this.color = this.lang.getConfigVal("color." + this.personType)
		if (this.personType === "patient") {
			this.color = patientColors[this.patient.id % patientColors.length]
		} else {
			this.sideItems =
				this.personType === "payor" ? PayorSiteItems : baseSideItems
		}

		if (this[this.personType].contactways) {
			this[this.personType].contactways.forEach((contactway) => {
				//iterate contactways
				contactway.contactway =
					contactway.contactway_dec ?? contactway.contactway //make sure descrypted value is being transfered
				contactway.name = contactway.name_dec ?? contactway.name //make sure descrypted value is being transfered
				contactway.link =
					(contactway.contactway_identifier == "phone" ? "tel:" : "mailto:") +
					contactway.contactway //create a link attribute (according to phone/mail differences)
			})
		}
		this.finresouecrActionSub?.unsubscribe()
		this.finresouecrActionSub = this.store.finresouecrActionSubj.subscribe(
			() => {
				if (this.finresourceTable) {
					this.finresourceTable.getItems()
				}
				this.store.postFinresourceRefresh(
					this.personType,
					this[this.personType]
				)
			}
		)
		this.payorAddListener?.unsubscribe()
		if (this.personType === "payor") {
			this.store.removeDecArr(this.payor.documents)

			this.payorAddListener = this.store.payorAddSubj.subscribe((res: any) => {
				if (res.payor_id != this.payor?.id) {
					return
				}
				const newCollection = [
					this.store.removeDecItem(res.row),
					...this.payor[res.tableName],
				]
				this.payor[res.tableName] = newCollection
				switch (res.tableName) {
					case "documents":
						this.leftSideUpdate(newCollection, "documents")
						if (this.curCenter == "document") {
							this.documentsTable.getItems()
						}
						break
				}
			})
		}
		this.personReloadListener?.unsubscribe()
		this.personReloadListener = this.store.personReloadSubj.subscribe(
			(res: any) => {
				const { personType, id } = res
				if (personType === this.personType && id == this.id) {
					this.id = +id
					this.preserveCenter = true
					this.onIdChange()
				}
			}
		)

		this.perpOtherSideCols()
	}
	perpOtherSideCols() {}

	ngOnChanges(changes: SimpleChange) {
		//if person changed (ex: contact) - send to load function
		if (changes[this.personType]) {
			this.load()
		}
		if (changes["id"]) {
			this.onIdChange()
		}
	}
	backToMain() {
		//reset current center content area
		this.curCenter = ""
		this.updateShowColumnLocal("center")
	}

	addInvoice() {
		const description =
			this.personType === "patient"
				? this.store.getAddInvoiceDescriptionDefault(
						this[this.personType].name,
						this[this.personType].id
					)
				: ""
		this.modalProps = {
			name: "add-invoice",
			description,
		}
	}
	handleAddInvoiceClose() {
		this.modalProps = null
		this.scrollTop()
	}

	openReport(report_name: string) {
		this.modalProps = {
			name: "reports",
			report_name,
		}
	}

	updateShowColumnLocal(col: HomePersonColumns) {
		switch (col) {
			case "center":
				this.scrollToCenter()
				break
			case "sideItems":
				this.scrollToSideItems()
				break
		}
		//update the currently shown column, and emit to parent that the value changed
		// this.showColumn = col
		// this.updateShowColumn.emit(col)
		// this.scrollTop()
	}
	scrollTop() {
		this.eventsService.scrollToTop()
		if (this.center && this.center.nativeElement) {
			this.center.nativeElement.scrollTop = 0
		}
	}
	scrollToCenter() {
		if (this.center?.nativeElement && this.eventsService.isMobile) {
			this.center.nativeElement.scrollIntoView()
			this.center.nativeElement.scrollTop = 0
			console.log("here1")
		}
	}
	scrollToSideItems() {
		if (this.sideItemsDiv?.nativeElement) {
			this.sideItemsDiv.nativeElement.scrollIntoView()
			this.sideItemsDiv.nativeElement.scrollTop = 0
		}
	}

	leftSideUpdate(rows: any[], tableToUpate: string) {
		//on receiving changes in collections (ex: contactways) from gen-tables etc., make sure to update the person (ex: contact) itself with those changes
		this[this.personType][tableToUpate] = [...rows]
	}

	getPerson() {
		return this[this.personType]
	}
}

@Component({
	selector: "app-contact",
	imports: [IMPORTS],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: "./person-common.component.html",
})
export class ContactComponent extends PersonCommonComponent {
	//inherits some fields and methods from PersonCommons!
	personType: ReportItemType = "contact" //used by PersonCommons to generalize
}

@Component({
	imports: [IMPORTS],
	selector: "app-payor",
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: "./person-common.component.html",
})
export class PayorComponent extends PersonCommonComponent {
	//inherits some fields and methods from PersonCommons!
	personType: ReportItemType = "payor" //used by PersonCommons to generalize
}
